import { createAsyncThunk } from '@reduxjs/toolkit';

import { navigateToBasket } from 'src/redux/productInfo/thunks';
import type { ReduxApplicationState } from 'src/redux/types';
import * as basketAPIMutations from 'src/services/basket-api/mutations';
import * as basketAPIQueries from 'src/services/basket-api/queries';
import type {
  BasketAPIBasketQuery,
  BasketAPIBasketWithItemsQuery,
  BasketAPIItem,
  BasketAPIMoney,
  BasketAPIMutationTrackingData,
} from 'src/services/basket-api/types/BasketAPIResponseBody';
import type { ProductOptionInputWithoutLabel } from 'src/services/basket-api/types/graphql';
import type { CatServiceAPIProductOptionValue } from 'src/services/cat-service-api/types/CatServiceAPIProductOption';
import type { SelectedPersonalisationsRecord } from '../personalisation/types';
import { actions as ProductInfoActions } from '../productInfo/slice';
import { dispatchBasketUpdatedEvent } from './domEvents';

export const getBasketForViewing = createAsyncThunk<BasketAPIBasketQuery>(
  'basket/getBasketForViewing',
  async (_, { getState }) => {
    const { user } = getState() as ReduxApplicationState;

    return await basketAPIQueries.getBasket(user.currencyCode);
  },
);

export const getBasketForEditing = createAsyncThunk<BasketAPIBasketWithItemsQuery>(
  'basket/getBasketForEditing',
  async (_, { getState }) => {
    const { user } = getState() as ReduxApplicationState;

    return await basketAPIQueries.getBasketWithItemOptions(user.currencyCode);
  },
);

export const mapProductOptionsToBasketOptions = (
  selectedPersonalisations: SelectedPersonalisationsRecord,
): ProductOptionInputWithoutLabel =>
  Object.keys(selectedPersonalisations).flatMap(
    (optionId) =>
      /* istanbul ignore next */
      selectedPersonalisations?.[optionId]?.map(
        ({ id, isCustomInput, name }: CatServiceAPIProductOptionValue) =>
          isCustomInput ? { optionId, value: name } : { optionId, valueId: String(id) },
      ) ?? [],
  );

interface AddItemToBasketPayload {
  basket: {
    discountedItemsTotal: BasketAPIMoney;
    itemsCount: number;
    itemsTotal: BasketAPIMoney;
  };
  trackingData: BasketAPIMutationTrackingData;
}

export const addItemToBasket = createAsyncThunk<AddItemToBasketPayload>(
  'basket/addItemToBasket',
  async (_, { dispatch, getState }) => {
    const {
      personalisation: { quantity, selectedPersonalisations },
      product: { code },
      user: { currencyCode },
    } = getState() as ReduxApplicationState;
    const options = mapProductOptionsToBasketOptions(selectedPersonalisations);

    const { basket, trackingData } = await basketAPIMutations.addItem(
      code,
      quantity,
      currencyCode,
      options,
    );

    dispatchBasketUpdatedEvent();

    dispatch(ProductInfoActions.openBasketModal());

    return {
      basket,
      trackingData,
    };
  },
);

interface UpdateItemInBasketPayload {
  basket: {
    discountedItemsTotal: BasketAPIMoney;
    items: BasketAPIItem[];
    itemsCount: number;
  };
  trackingData: BasketAPIMutationTrackingData;
}

export const updateItemInBasket = createAsyncThunk<UpdateItemInBasketPayload>(
  'basket/updateItemInBasket',
  async (_, { dispatch, getState }) => {
    const {
      personalisation: { editedItem, quantity, selectedPersonalisations },
      user: { currencyCode },
    } = getState() as ReduxApplicationState;
    const options = mapProductOptionsToBasketOptions(selectedPersonalisations);

    const { basket, trackingData } = await basketAPIMutations.updateItem(
      editedItem!,
      quantity,
      currencyCode,
      options,
    );

    dispatch(navigateToBasket());

    return {
      basket,
      trackingData,
    };
  },
);

interface AddMembershipItemToBasketPayload {
  basket: BasketAPIBasketWithItemsQuery;
}

export const addMembershipItemToBasket = createAsyncThunk<AddMembershipItemToBasketPayload>(
  'basket/addMembershipItemToBasket',
  async (_, { getState }) => {
    const {
      user: { currencyCode },
    } = getState() as ReduxApplicationState;

    const { basket } = await basketAPIMutations.addMembershipItem(currencyCode);

    dispatchBasketUpdatedEvent();

    return { basket };
  },
);
